import React, { useMemo } from 'react';
import { LuFolderKanban } from 'react-icons/lu';
import { dateFormat } from '../../../utils/dateFormat';
import { ProjectStatusBadge, ProjectTypeBadge } from '../../common';
import { ActionMenu } from '../../ui';
import { deriveProjectHealth, formatPassRate, hasRepositoryLinked } from './projectHealth';
import { buildProjectMenuItems } from './projectMenu';

function HealthPill({ health }) {
  if (!health) return null;
  return (
    <span className={`rt-proj-health rt-proj-health--${health.tone}`} title={health.label}>
      <span className="rt-proj-health__dot" aria-hidden />
      {health.label}
    </span>
  );
}

/**
 * Project discovery card — primary CTA is Open Project.
 * Overflow menu is secondary (settings, import link, repo, requirements, archive, delete).
 */
export default function ProjectCard({
  project,
  onOpen,
  onEdit,
  onIntegration,
  onLinkRepo,
  onCrawl,
  bitbucketConfigured = false,
  onDuplicate,
  onArchive,
  onUnarchive,
  onActivate,
  onDelete,
}) {
  const health = useMemo(() => deriveProjectHealth(project), [project]);
  const passRate = formatPassRate(project);
  const repoLinked = hasRepositoryLinked(project);
  const cases = Number(project?.testCases || 0);
  const updated = project?.updatedAt || project?.createdAt;

  const menuItems = useMemo(
    () =>
      buildProjectMenuItems(project, {
        bitbucketConfigured,
        onEdit,
        onIntegration,
        onLinkRepo,
        onCrawl,
        onDuplicate,
        onArchive,
        onUnarchive,
        onActivate,
        onDelete,
      }),
    [
      bitbucketConfigured,
      onActivate,
      onArchive,
      onCrawl,
      onDelete,
      onDuplicate,
      onEdit,
      onIntegration,
      onLinkRepo,
      onUnarchive,
      project,
    ]
  );

  return (
    <article className="rt-proj-card">
      <div className="rt-proj-card__top">
        <div className="rt-proj-card__identity">
          <span className="rt-proj-card__icon" aria-hidden>
            <LuFolderKanban size={18} />
          </span>
          <div className="rt-proj-card__titles">
            <button
              type="button"
              className="rt-proj-card__name"
              onClick={() => onOpen?.(project)}
              title={project.name}
            >
              {project.name}
            </button>
            <div className="rt-proj-card__badges">
              <ProjectTypeBadge type={project.type} />
              <ProjectStatusBadge status={project.status} />
              <HealthPill health={health} />
            </div>
          </div>
        </div>
      </div>

      {project.description ? (
        <p className="rt-proj-card__desc">{project.description}</p>
      ) : null}

      <div className="rt-proj-card__metrics">
        <div className="rt-proj-card__metric">
          <span className="rt-proj-card__metric-label">Test cases</span>
          <strong className="rt-proj-card__metric-value">
            {cases.toLocaleString()}
          </strong>
        </div>
        <div className="rt-proj-card__metric">
          <span className="rt-proj-card__metric-label">Pass rate</span>
          <strong className="rt-proj-card__metric-value">
            {passRate ?? '—'}
          </strong>
        </div>
        <div className="rt-proj-card__metric">
          <span className="rt-proj-card__metric-label">Repository</span>
          <strong className="rt-proj-card__metric-value">
            {repoLinked ? 'Connected' : 'Not linked'}
          </strong>
        </div>
      </div>

      <div className="rt-proj-card__meta">
        {updated ? <span>Updated {dateFormat(updated)}</span> : null}
        {project.owner && project.owner !== 'Unknown' ? (
          <span>Owned by {project.owner}</span>
        ) : null}
      </div>

      <div className="rt-proj-card__footer">
        <button
          type="button"
          className="rt-btn rt-btn--primary rt-btn--sm"
          onClick={() => onOpen?.(project)}
        >
          Open Project
        </button>
        <ActionMenu items={menuItems} label={`Actions for ${project.name}`} />
      </div>
    </article>
  );
}
